import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { Icon } from "~/assets/icons/Icon";
import {
  useAboutBreadcrumbs,
  useVisibilityEngineRoutes,
} from "~/components/siteSpeed/useVisibilityEngineRoutes";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import Button from "~/components/ui/button/Button";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider.tsx";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./SiteSpeedPage.module.scss";

const faqData = [
  {
    id: 1,
    question:
      "How technical does our team need to be to implement and manage this feature?",
    answer:
      "Our platform is built with user-friendliness in mind. You don't need deep technical expertise to implement or manage it. We provide a simple, intuitive interface and comprehensive support. Plus, our automation takes care of the complexities of SEO and website optimization, so you can focus on strategy and content, not the technical nitty-gritty.",
    defaultOpen: true,
  },
  {
    id: 2,
    question:
      "Will integrating this feature disrupt my clients' website operations or uptime?",
    answer:
      "The transition to using our feature is smooth and does not interfere with your clients' website operations or uptime. Our approach ensures that optimizations are applied without any service interruptions, allowing you to maintain the high-quality service standards your clients expect.",
  },
  {
    id: 3,
    question:
      "Will this feature require a lot of ongoing maintenance or adjustments from our end?",
    answer:
      "Once set up, our feature operates with minimal need for oversight. The AI-driven optimization is designed to adapt and respond to your clients' website changes in real-time, meaning maintenance from your team is substantially reduced. This allows your agency to allocate resources more efficiently and focus on growth.",
  },
  {
    id: 4,
    question:
      "Can this feature handle websites with high traffic volumes without compromising on performance?",
    answer:
      "Whether your clients are small businesses or high-traffic e-commerce sites, our optimization process can handle significant loads while continuously improving performance. This scalability ensures that as your clients grow, our solution grows with them, providing consistent benefits regardless of traffic volume.",
  },
  {
    id: 5,
    question: "Do I need to change my website platform or CMS?",
    answer:
      "No changes required. The Search Visibility Engine works with any platform—WordPress, Shopify, React apps, or custom builds. Simply install our JavaScript snippet and the engine handles everything automatically.",
  },
  {
    id: 6,
    question: "How quickly will I see results?",
    answer:
      "Most sites see immediate improvements in crawler accessibility within 24 hours. AI systems typically begin citing newly visible content within 7-14 days, depending on their crawl schedules and content relevance.",
  },
  {
    id: 7,
    question: "Can this help with sites already blocked by Cloudflare?",
    answer:
      "Yes. Our engine includes intelligent crawler management that properly identifies legitimate search and AI crawlers to security tools, ensuring they're whitelisted while maintaining protection against malicious bots.",
  },
];

const FAQItem = ({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  return (
    <div className={styles.faqItem}>
      <div
        className={styles.faqHeader}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isOpen}
      >
        <span className={styles.faqQuestion}>{question}</span>
        <div
          className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ""}`}
        >
          <Icon iconName="IconDown" width="1rem" height="1rem" />
        </div>
      </div>
      {isOpen && <div className={styles.faqAnswer}>{answer}</div>}
    </div>
  );
};

const AboutPage = () => {
  usePageTitle(PAGE_TITLES.aboutAiVisibilityEngine);

  const { team } = useTeam();
  const veRoutes = useVisibilityEngineRoutes();
  const breadcrumbs = useAboutBreadcrumbs();

  const [openFAQs, setOpenFAQs] = useState<Set<number>>(
    new Set(faqData.filter((item) => item.defaultOpen).map((item) => item.id)),
  );

  const toggleFAQ = (id: number) => {
    const newOpenFAQs = new Set(openFAQs);
    if (openFAQs.has(id)) {
      newOpenFAQs.delete(id);
    } else {
      newOpenFAQs.add(id);
    }
    setOpenFAQs(newOpenFAQs);
  };

  if (team && !team.subscription?.price?.product?.prerendering) {
    return <Navigate to={routes.home()} replace />;
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.aboutAiVisibilityEngine}</h1>
      {breadcrumbs && <Breadcrumbs fakeBreadcrumbs={breadcrumbs} />}

      <div className={styles.aboutContent}>
        <div className={styles.heroSection}>
          <div className={styles.textContent}>
            <h2 className={styles.welcomeTitle}>
              Welcome to Your AI Visibility Engine
            </h2>

            <p className={styles.subtitle}>
              Ready to unlock the LLM potential of your website?
            </p>

            <p className={styles.description}>Imagine...</p>

            <ul className={styles.benefitsList}>
              <li>
                <strong>Your Website, LLM Friendly:</strong> LLM bots like
                ChatGPT don't execute scripts, so they can't see content on many
                websites. We serve a special version of your website to bots
                that pre-renders all content into LLM bot-readable static HTML.
              </li>
              <li>
                <strong>Increase Crawl Budget for SEO and GEO:</strong> Our file
                compression, HTTP/3 support, resource pre-rendering and
                optimized server request path infrastructure can load websites
                up to 80% faster. That means happier bots visiting more pages
                and seeing more of your content.
              </li>
              <li>
                <strong>Total Automation Integration:</strong> Plus, the On-Page
                Automations you have enabled in our dashboard are served
                automatically and pre-rendered for LLM and search engine bot
                processing.
              </li>
              <li>
                <strong>Stop Blocking ChatGPT:</strong> ChatGPT bot is the
                second most blocked bot on the internet. Many businesses don't
                realize their infrastructure providers are blocking OpenAI and
                others. We'll take over all bot requests so your content can get
                seen.
              </li>
            </ul>

            <Link to={veRoutes.domains()}>
              <Button className={styles.configureButton}>
                Configure Domains
              </Button>
            </Link>
          </div>

          <div className={styles.heroImage}>
            <img
              src="/images/analytics-eacaf13f0d2451a36d60d3871cc95c38ba52d59bbe3b39772edafed4c68cd5ab.svg"
              alt="Analytics dashboard illustration"
              className={styles.heroIllustration}
            />
          </div>
        </div>

        <div className={styles.infoSections}>
          <div className={styles.infoSection}>
            <div className={styles.sectionContent}>
              <h3 className={styles.sectionTitle}>Quick and Simple Setup</h3>

              <p className={styles.setupDescription}>
                Simply follow the step-by-step instructions provided within the
                wizard.
              </p>

              <p className={styles.setupDescription}>
                Our process is designed to be user-friendly and doesn't require
                any prior technical knowledge or expertise.
              </p>

              <p className={styles.setupDescription}>
                We've made it easy for anyone to get started, regardless of
                their technical background.
              </p>

              <p className={styles.setupDescription}>
                Got questions or need assistance? Our support team is ready to
                help.
              </p>

              <ul className={styles.supportList}>
                <li>
                  <strong>Help Center:</strong> Access guides and FAQs for
                  instant support.
                </li>
                <li>
                  <strong>Live Chat:</strong> Connect with our support team for
                  real-time assistance.
                </li>
              </ul>
            </div>

            <div className={styles.sectionImage}>
              <img
                src="/images/how-it-works-f848d36564b5e968b3d05ff705dae61d420cadd1b5138a17e561134d02047e91.svg"
                alt="Setup wizard illustration"
                className={styles.sectionIllustration}
              />
            </div>
          </div>
        </div>

        <div className={styles.faqSection}>
          <h3 className={styles.faqTitle}>Frequently asked questions</h3>
          <div className={styles.faqContainer}>
            {faqData.map((faq) => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQs.has(faq.id)}
                onToggle={() => toggleFAQ(faq.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
